// The House of Chopras - Main Entry Point
// A labyrinthine, interactive experience mirroring the novel's themes

import './styles/global.css';
import './styles/typography.css';
import './styles/components.css';
import './styles/transitions.css';
import './styles/components/signal-lost.css';
import './styles/components/arg-terminal.css';
import './styles/pages/entry.css';
import './styles/pages/house.css';
import './styles/pages/author.css';
import './styles/pages/videos.css';
import './styles/pages/press.css';
import './styles/pages/purchase.css';
import './styles/pages/guestbook.css';
import './styles/pages/basement.css';
import './styles/pages/attic.css';
import './styles/pages/archive.css';
import './styles/pages/market.css';
import './styles/pages/crypt.css';
import { router, type Route } from './scripts/router';
import { transitionManager } from './scripts/transitions';
import { initIdleDetection } from './scripts/idle';

// Import page modules
import { renderEntry, cleanupEntry } from './pages/entry';
import { renderHouse, cleanupHouse } from './pages/house';
import { renderAuthor, cleanupAuthor } from './pages/author';
import { renderVideos, cleanupVideos } from './pages/videos';
import { renderPress, cleanupPress } from './pages/press';
import { renderPurchase, cleanupPurchase } from './pages/purchase';
import { renderGuestbook, cleanupGuestbook } from './pages/guestbook';

// Import hidden page modules
import { renderBasement, cleanupBasement } from './pages/hidden/basement';
import { renderAttic, cleanupAttic } from './pages/hidden/attic';
import { renderArchive, cleanupArchive } from './pages/hidden/archive';
import { renderMarket, cleanupMarket } from './pages/hidden/market';
import { renderCrypt, cleanupCrypt } from './pages/hidden/crypt';

// Constants for localStorage keys
const STORAGE_KEYS = {
  INTRO_SEEN: 'chopras_intro_seen',
  REDUCED_MOTION: 'chopras_reduced_motion',
} as const;

/**
 * Check if user prefers reduced motion
 * Note: Always returns false to ensure full experience with effects
 */
function prefersReducedMotion(): boolean {
  return false;
}

/**
 * Apply reduced motion class to body if needed
 */
function applyMotionPreference(): void {
  if (prefersReducedMotion()) {
    document.body.classList.add('reduced-motion');
  } else {
    document.body.classList.remove('reduced-motion');
  }
}

/**
 * Listen for system motion preference changes
 */
function setupMotionPreferenceListener(): void {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  mediaQuery.addEventListener('change', () => {
    // Only apply if no localStorage override
    if (localStorage.getItem(STORAGE_KEYS.REDUCED_MOTION) === null) {
      applyMotionPreference();
    }
  });
}

/**
 * Check if intro has been seen before
 */
export function hasSeenIntro(): boolean {
  return localStorage.getItem(STORAGE_KEYS.INTRO_SEEN) === 'true';
}

/**
 * Mark intro as seen
 */
export function markIntroSeen(): void {
  localStorage.setItem(STORAGE_KEYS.INTRO_SEEN, 'true');
}

/**
 * Clear intro seen state (for testing/development)
 */
export function clearIntroSeen(): void {
  localStorage.removeItem(STORAGE_KEYS.INTRO_SEEN);
}

// Register routes with render and cleanup functions
const routes: Route[] = [
  // Main pages
  { path: '', render: renderEntry, cleanup: cleanupEntry },
  { path: 'house', render: renderHouse, cleanup: cleanupHouse },
  { path: 'author', render: renderAuthor, cleanup: cleanupAuthor },
  { path: 'videos', render: renderVideos, cleanup: cleanupVideos },
  { path: 'press', render: renderPress, cleanup: cleanupPress },
  { path: 'purchase', render: renderPurchase, cleanup: cleanupPurchase },
  { path: 'guestbook', render: renderGuestbook, cleanup: cleanupGuestbook },

  // Hidden easter egg routes
  { path: 'basement', render: renderBasement, cleanup: cleanupBasement, hidden: true },
  { path: 'attic', render: renderAttic, cleanup: cleanupAttic, hidden: true },
  { path: 'archive', render: renderArchive, cleanup: cleanupArchive, hidden: true },
  { path: 'market', render: renderMarket, cleanup: cleanupMarket, hidden: true },
  { path: 'crypt', render: renderCrypt, cleanup: cleanupCrypt, hidden: true },
];

/**
 * Setup skip link functionality for keyboard navigation
 * The skip link allows keyboard users to bypass navigation and jump to main content
 */
function setupSkipLink(): void {
  const skipLink = document.getElementById('skip-link');
  if (!skipLink) return;

  skipLink.addEventListener('click', (e) => {
    e.preventDefault();

    // Find the main content target on the current page
    const mainContent = document.getElementById('main-content')
      || document.querySelector('[data-focus-target]')
      || document.querySelector('main')
      || document.querySelector('[role="main"]')
      || document.querySelector('.page-container h1');

    if (mainContent instanceof HTMLElement) {
      // Make it focusable if not already
      if (!mainContent.hasAttribute('tabindex')) {
        mainContent.setAttribute('tabindex', '-1');
      }
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });
}

/**
 * Initialize the application
 */
function init(): void {
  // Apply motion preference
  applyMotionPreference();
  setupMotionPreferenceListener();

  // Setup skip link for accessibility
  setupSkipLink();

  // Register all routes
  router.register(routes);

  // Set up page transition handler for animated route changes
  router.setTransitionHandler((from, to, renderNewPage) => {
    return transitionManager.transition(from, to, renderNewPage);
  });

  // Initialize idle detection for hidden section reveals
  initIdleDetection();

  // Start routing - this will handle initial page load
  // The router constructor already sets up hashchange and load listeners,
  // but we call navigate() to ensure initial render happens
  router.navigate();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
